import type { RulePlanExistingOutput, RuleResult, RuleView } from "@kwp/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, useTRPC } from "../../../lib/api";
import { Button, cx, ErrorText, PageHeader, Section, StatusText, Switch } from "../../../ui";
import { useToast } from "../../../ui/toast";
import { newRequestId } from "../../tasks/use-task-actions";
import { SettingsNav } from "../SettingsNav";
import { ApplyExistingDialog } from "./ApplyExistingDialog";
import { describeAction, draftFromRule, EMPTY_DRAFT, type RuleDraft } from "./model";
import { RuleDialog } from "./RuleDialog";

function ModeBadge({ mode }: { mode: RuleView["mode"] }) {
  return (
    <span
      className={cx("inline-block rounded-full px-2 py-0.5 text-xs font-medium", mode === "native" ? "bg-surface-2 text-text" : "border border-border text-muted")}
      title={mode === "native" ? "Runs as a Gmail filter / Outlook rule, even when KitWork+ is down" : "Runs in KitWork+ on new mail"}
    >
      {mode === "native" ? "Native" : "App"}
    </span>
  );
}

/**
 * Settings → Rules (architecture §7): every rule with how it runs (native in the provider, or in
 * KitWork+), its errors and an on/off switch; create and edit in a dialog with a live preview;
 * apply a rule to mail that already matches; remove (undoable from Activity).
 */
export function RulesPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const rules = useQuery(trpc.rules.list.queryOptions());
  const accounts = useQuery(trpc.accounts.list.queryOptions());
  const [editing, setEditing] = useState<RuleDraft | null>(null);
  const [plans, setPlans] = useState<{ name: string; plans: RulePlanExistingOutput } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.rules.list.queryKey() });
  const fail = (error: unknown) => toast({ tone: "error", message: errorMessage(error) });

  const setEnabled = useMutation(
    trpc.rules.setEnabled.mutationOptions({
      onSuccess: async (out) => {
        const r = out.status === "done" ? (out.result as RuleResult) : null;
        if (r !== null && r.lastError !== null) toast({ tone: "error", message: `Kept off: ${r.lastError}` });
        await refresh();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    trpc.rules.remove.mutationOptions({
      onSuccess: async () => {
        toast({ message: "Rule removed. Undo it from Activity." });
        await refresh();
      },
      onError: fail,
    }),
  );
  const planExisting = useMutation(trpc.rules.planExisting.mutationOptions({ onError: fail }));

  const addresses = new Map((accounts.data === undefined ? [] : accounts.data).map((a) => [a.id, a.address]));
  const allIds = [...addresses.keys()];
  const noAccounts = accounts.data !== undefined && accounts.data.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Rules">
        <Button variant="primary" disabled={accounts.data === undefined || noAccounts} onClick={() => setEditing(EMPTY_DRAFT)}>
          New rule
        </Button>
      </PageHeader>
      <SettingsNav />
      <Section
        title="Mail rules"
        description="Rules act on new mail. Native rules run in Gmail or Outlook, even when KitWork+ is down; app rules (what the provider cannot express) run in KitWork+."
      >
        {noAccounts ? <p className="text-sm text-muted">Connect a mail account first.</p> : null}
        {rules.isPending ? <StatusText>Loading…</StatusText> : null}
        {rules.isError ? <ErrorText>{errorMessage(rules.error)}</ErrorText> : null}
        {rules.data !== undefined && rules.data.length === 0 ? <p className="text-sm text-muted">No rules yet.</p> : null}
        {rules.data !== undefined && rules.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Mail rules</caption>
              <thead className="text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Rule
                  </th>
                  <th scope="col" className="hidden py-2 pr-3 font-medium md:table-cell">
                    On
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    Runs
                  </th>
                  <th scope="col" className="py-2 pr-3 font-medium">
                    <span className="sr-only">Enabled</span>
                  </th>
                  <th scope="col" className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.data.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="py-2 pr-3">
                      <button type="button" className="font-medium text-text underline-offset-2 hover:underline" onClick={() => setEditing(draftFromRule(r, allIds))}>
                        {r.name}
                      </button>
                      <p className="break-words text-muted">
                        <code>{r.query}</code> → {r.actions.map(describeAction).join(", ")}
                      </p>
                      {r.lastError !== null ? <p className="text-danger">{r.lastError}</p> : null}
                    </td>
                    <td className="hidden py-2 pr-3 text-muted md:table-cell">{r.accountIds.map((id) => addresses.get(id)).filter((a) => a !== undefined).join(", ")}</td>
                    <td className="py-2 pr-3">
                      <ModeBadge mode={r.mode} />
                    </td>
                    <td className="py-2 pr-3">
                      <Switch
                        checked={r.enabled}
                        disabled={setEnabled.isPending}
                        label={`${r.enabled ? "On" : "Off"}`}
                        onChange={(enabled) => setEnabled.mutate({ input: { ruleId: r.id, enabled }, requestId: newRequestId() })}
                      />
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        aria-label={`Apply ${r.name} to existing mail`}
                        disabled={planExisting.isPending}
                        onClick={() => planExisting.mutate({ ruleId: r.id }, { onSuccess: (p) => setPlans({ name: r.name, plans: p }) })}
                      >
                        Apply to existing
                      </Button>
                      <Button variant="danger" aria-label={`Remove ${r.name}`} disabled={remove.isPending} onClick={() => remove.mutate({ input: { ruleId: r.id }, requestId: newRequestId() })}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Section>
      {editing === null ? null : <RuleDialog draft={editing} onClose={() => setEditing(null)} />}
      {plans === null ? null : <ApplyExistingDialog ruleName={plans.name} plans={plans.plans} onClose={() => setPlans(null)} />}
    </div>
  );
}
